import { FILE_TYPE } from '../constant/numericParams';
import {
  ICourseMaterialReferenceDocument,
  ICreateCoursePayload,
  WatermarkPosition,
} from './course.interface';
import { ECourseStatus } from './doccen-lib/contentManagement.interface';

//this to prevent changing too many old files
export { ECourseStatus as CourseStatusEnum } from '@/interfaces/doccen-lib/contentManagement.interface';

export interface IFolderInfo {
  name: string;
  description: string;
  status: FolderStatusEnum;
}

export type TFolderHashtag = { name: string; id: string };

export interface IFileInfo {
  name: string;
}

export enum RoleViewEnum {
  Editor = 'editor',
  Viewer = 'viewer',
}

export type RoleViewType = `${RoleViewEnum}`;

export enum PlaceSearchEnum {
  Dashboard = 'dashboard',
  Shared = 'shared',
  Trash = 'trash',
}

export type PlaceSearchType = `${PlaceSearchEnum}`;

export type TFileType = `${FILE_TYPE}`;

export interface IGeneralTMSSearchData {
  advancedSearch?: {
    fields: string[];
    keyword: 'string';
  };
  keyword: 'string';
  pageNumber?: number;
  pageSize?: number;
  orderBy?: string[];
}

export interface IFilesSearchData extends IGeneralTMSSearchData {
  place: PlaceSearchType;
  fileType: TFileType;
}

export interface IFilesSearchInFolder extends IGeneralTMSSearchData {
  folderId: 'string';
  fileType: TFileType;
}

export interface IFoldersSearchData extends IGeneralTMSSearchData {
  place: PlaceSearchType;
}

export interface ICourseSearchData extends IGeneralTMSSearchData {}

export enum CourseCertificateEnum {
  CME = 'CME',
  CPE = 'CPE',
}

export type TCourseCertificate = `${CourseCertificateEnum}` | null;

export interface IPublishCourseBarProps {
  status: ECourseStatus | undefined;
  certificateType: string | null | undefined;
}

export type TCourseStatusEnum = `${CourseCertificateEnum}`;

export interface IRequestCertificateBarProps {
  course: { [key: string]: any };
  certificate: string | null | undefined;
}

export enum FolderStatusEnum {
  Public = 'Public',
  Private = 'Private',
}

export enum GroupSuggestionActionEnum {
  JoinGroup = 'JoinGroup',
  ShowRole = 'ShowRole',
}

export enum CourseMaterialEnum {
  Lesson = 'Lesson',
  Quiz = 'Quiz',
  H5P = 'H5P',
}

export type TNavigateMaterial = {
  id: string;
  title: string;
  type: CourseMaterialEnum;
  isTrial: boolean;
} | null;

export interface IUpdateLessonInfoPayload {
  id: string;
  title: string;
  description: string | null;
  file: File | null;
  isUpdateThumbnail: boolean;
}

export interface ILessonDetail
  extends Partial<ICourseMaterialReferenceDocument> {
  completionTimes: number;
  courseId: string;
  courseTitle: string;
  createdOn: string;
  description: string;
  duration: string;
  file: {
    id: string;
    name: string;
    type: string;
    size: number;
    url: string;
  };
  fileId: string;
  id: string;
  isCompleted: boolean;
  isCourseCompleted: boolean;
  isDeleted: boolean;
  materialType: CourseMaterialEnum;
  previousMaterial: TNavigateMaterial;
  nextMaterial: TNavigateMaterial;
  orderInCourse: number;
  status: string;
  thumbnail: string;
  title: string;
  url: string;
  viewCount: number;
  lastViewDuration: string;
  // only have value when type is H5P
  contentId: string | null;
  contentType: string | null;
  watermark: WatermarkPosition;
  isTrial: boolean;
}
export interface ILessonManagementDraggableList {
  courseMaterials: TCourseMaterial[] | undefined;
  status: string | undefined;
  certificateType: string | null | undefined;
  allowTrial?: boolean;
}

interface IManagementColumns {
  id: string;
  label: string | null;
  format?: (_value: any, _allowTrial?: boolean) => any;
  maxWidth?: number;
  minWidth?: number;
  align?: 'left' | 'right' | 'center';
}

export interface ILessonManagementColumns extends IManagementColumns {}

export interface IQuizManagementColumns extends IManagementColumns {}

export type TRedirectCourseMatetrialProps = {
  id?: string;
  materialType?: CourseMaterialEnum;
  title?: string;
  isTrial?: boolean;
} | null;

export enum EOrderStatus {
  Paid = 'Paid',
  Failed = 'Failed',
  Pending = 'Pending',
}

export interface ICourseDetailResponse
  extends Pick<
    ICreateCoursePayload,
    'allowVideoSkipping' | 'allowLessonSkipping'
  > {
  author: {
    avatar: string | null;
    name: string;
    profileUrl: string;
    academicRank: string | null;
  };
  isOwner: boolean;
  group: {
    id: string;
    name: string;
    status: FolderStatusEnum;
  } | null;
  allowTrial: boolean;
  id: string;
  title: string;
  description: string | null;
  thumbnail: string | null;
  status: ECourseStatus;
  certificateShortName: CourseCertificateEnum | null;
  lastModifiedOn: string;
  lessonCount: 0;
  totalDuration: string;
  courseMaterials: TCourseMaterial[];
  coursePrice: {
    /** mean this course is must-paid-to-use */
    isPaidCourse: boolean;
    price: number;
  } | null;
  userOrder: {
    hasUserPaid: boolean;
    purchaseDate: string | null;
    status: EOrderStatus;
    orderId: string;
  } | null;
  requiresVerified: boolean;
  userProgress: {
    canComplete: boolean;
    hasAgreementConfirmed: boolean;
    isCertificateRequested: boolean;
    isCompleted: boolean;
  } | null;
  watermark: WatermarkPosition;
  category: {
    category: ICourseDetailResponse['category'] | null;
    id: number;
    name: string;
  };
}

export interface IQuizCourseMaterial {
  name?: string;
  duration: string;
  completionTimes: number;
  viewCount: number;
  previousMaterial: null;
  nextMaterial: null;
  id: string;
  materialType: CourseMaterialEnum;
  isCompleted: boolean;
  orderInCourse: number;
  isTrial: boolean;
}

export type TCourseMaterial = IQuizCourseMaterial | ILessonDetail;

export interface IGeneralCourseInfoProps {
  course: ICourseDetailResponse | undefined;
  canEdit: boolean;
  courseId: string;
}

export interface ITrackingLessonPayload {
  lessonId: string;
  courseId: string;
  lastViewDuration: string;
}

export interface ITrackingLessonResponse {
  isCompleted: boolean;
  isCourseCompleted: boolean;
  lastViewDuration: string;
  totalViewDuration: string;
}
